import React, { useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";
import HeroSection from "../components/HeroSection";
import CounterSection from "../components/CounterSection";
import "./Home.css";

const Home = () => {
  const solutions = [
    {
      title: "Demand Data",
      description: "Accurate U.S. account and contact data mapped to buying committees and intent signals.",
    },
    {
      title: "Campaign Engine",
      description: "Multi-channel programs for outbound, nurture, and reactivation with clear performance tracking.",
    },
    {
      title: "Revenue Operations",
      description: "Pipeline process design, lead routing, and conversion optimization across marketing and sales.",
    },
    {
      title: "Automation Stack",
      description: "Workflow automation and AI support to reduce manual effort and improve execution speed.",
    },
  ];

  const playbook = [
    "Discover goals, market, and funnel constraints",
    "Design a measurable plan with weekly milestones",
    "Deploy campaigns and operational workflows",
    "Optimize continuously using conversion and revenue data",
  ];

  const testimonials = [
    {
      name: "Michael Thompson",
      quote:
        "ThinkRise USA helped us increase qualified leads by 50% in just three months. Their approach is strategic, data-driven, and relentlessly focused on outcomes.",
      title: "Head of Marketing",
      company: "US-based SaaS Company",
      avatar: "https://i.pravatar.cc/160?img=12",
    },
    {
      name: "Emma Richardson",
      quote:
        "ThinkRise USA understood our challenges and built a tailored plan that made a measurable difference in our pipeline and conversion quality.",
      title: "Growth Director",
      company: "B2B Consulting Firm",
      avatar: "https://i.pravatar.cc/160?img=47",
    },
  ];

  const testimonialSliderRef = useRef(null);

  useEffect(() => {
    const slider = testimonialSliderRef.current;
    if (!slider) return;

    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    if (reduceMotion) return;

    const interval = window.setInterval(() => {
      const cards = slider.querySelectorAll?.("[data-testimonial-card='true']");
      if (!cards?.length) return;

      const currentLeft = slider.scrollLeft;
      const currentIndex = Array.from(cards).findIndex((card) => {
        const cardLeft = card.offsetLeft;
        return Math.abs(cardLeft - currentLeft) < 10;
      });

      const nextIndex = currentIndex >= 0 ? (currentIndex + 1) % cards.length : 1 % cards.length;
      const nextLeft = cards[nextIndex]?.offsetLeft ?? 0;
      slider.scrollTo({ left: nextLeft, behavior: "smooth" });
    }, 4200);

    return () => window.clearInterval(interval);
  }, []);

  const partners = [
    {
      name: "Oracle",
      domain: "oracle.com",
      detail: "Cloud Infrastructure & Database",
    },
    {
      name: "SAP",
      domain: "sap.com",
      detail: "Enterprise Resource Planning",
    },
    {
      name: "Dun & Bradstreet",
      domain: "dnb.com",
      detail: "Business Data & Analytics",
    },
    {
      name: "Marketo",
      domain: "marketo.com",
      detail: "Marketing Automation Platform",
    },
  ];

  const partnerLogoUrl = (domain) =>
    `https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=128`;

  return (
    <>
      <Helmet>
        <title>ThinkRise USA | Official B2B Data &amp; Marketing Company</title>
        <meta
          name="description"
          content="ThinkRise USA is the official B2B data &amp; marketing company delivering verified U.S. contact data, demand generation, and revenue growth programs."
        />
        <link rel="canonical" href="https://thinkriseusa.com/" />

        <meta property="og:title" content="ThinkRise USA | Official B2B Data &amp; Marketing Company" />
        <meta
          property="og:description"
          content="ThinkRise USA is the official B2B data &amp; marketing company delivering verified U.S. contact data, demand generation, and revenue growth programs."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://thinkriseusa.com/" />

        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="ThinkRise USA | Official B2B Data &amp; Marketing Company" />
        <meta
          name="twitter:description"
          content="ThinkRise USA is the official B2B data &amp; marketing company delivering verified U.S. contact data, demand generation, and revenue growth programs."
        />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "ThinkRise USA",
            url: "https://thinkriseusa.com/",
            logo: "https://thinkriseusa.com/favicon.ico",
          })}
        </script>
      </Helmet>
      <HeroSection />
      <CounterSection style={{ padding: "40px 0", margin: 0 }} />

      <main>
        <section className="section home-intro-section">
          <div className="container">
            <h2 className="section-title">Built for U.S. Growth Teams</h2>
            <div className="home-intro-card">
              <p>
                ThinkRise USA helps B2B organizations move from scattered tactics to structured growth systems.
                We combine high-confidence data, campaign strategy, automation, and consulting to improve pipeline quality.
              </p>
              <p>
                Every engagement is outcome-first: better conversion, faster sales cycles, and stronger revenue predictability.
              </p>
            </div>
          </div>
        </section>

        <section className="section home-solutions-section">
          <div className="container">
            <h2 className="section-title">Core Solution Cards</h2>
            <div className="home-solutions-grid">
              {solutions.map((item, index) => (
                <article key={item.title} className={`home-solution-card tone-${index + 1}`}>
                  <span className="card-index">0{index + 1}</span>
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="section home-process-section">
          <div className="container">
            <h2 className="section-title">How We Execute</h2>
            <div className="home-process-list">
              {playbook.map((step, index) => (
                <div key={step} className="home-process-item">
                  <span className="process-dot">{index + 1}</span>
                  <p>{step}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="section home-results-section">
          <div className="container">
            <h2 className="section-title">Proven Results with Our Clients</h2>
            <div className="home-testimonial-slider" ref={testimonialSliderRef} aria-label="Client testimonials">
              {testimonials.map((item) => (
                <article
                  key={item.name}
                  className="home-testimonial-card"
                  data-testimonial-card="true"
                  aria-label={`Testimonial from ${item.name}`}
                >
                  <div className="home-testimonial-header">
                    <img
                      className="home-testimonial-avatar"
                      src={item.avatar}
                      alt={`${item.name} portrait`}
                      loading="lazy"
                      decoding="async"
                      referrerPolicy="no-referrer"
                    />
                    <div className="home-testimonial-meta">
                      <h3>{item.name}</h3>
                      <p>
                        {item.title}
                        <span className="home-testimonial-divider">•</span>
                        {item.company}
                      </p>
                    </div>
                  </div>
                  <div className="home-testimonial-quote">“{item.quote}”</div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="section home-partners-section">
          <div className="container">
            <h2 className="section-title">Our Trusted Partners</h2>
            <p className="home-section-subtitle">
              We collaborate with industry leaders to deliver exceptional solutions.
            </p>

            <div className="home-partners-marquee" aria-label="Trusted partners">
              <div className="home-partners-track">
                {[...partners, ...partners].map((partner, index) => (
                  <article
                    key={`${partner.name}-${index}`}
                    className="home-partner-card"
                    aria-label={partner.name}
                  >
                    <div className="home-partner-logo-wrap">
                      <img
                        src={partnerLogoUrl(partner.domain)}
                        alt={`${partner.name} logo`}
                        loading="lazy"
                        decoding="async"
                        onError={(event) => {
                          event.currentTarget.style.visibility = "hidden";
                        }}
                      />
                      <span className="home-partner-fallback" aria-hidden="true">
                        {partner.name
                          .split(" ")
                          .filter(Boolean)
                          .slice(0, 2)
                          .map((part) => part[0])
                          .join("")}
                      </span>
                    </div>
                    <div className="home-partner-meta">
                      <h3>{partner.name}</h3>
                      <p>{partner.detail}</p>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="section home-tagline-section">
          <div className="container">
            <h2 className="home-tagline">Think Smarter. Build Pipeline. Rise Faster.</h2>
          </div>
        </section>
      </main>
    </>
  );
};

export default Home;
